import { forwardRef, lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  AnalyticsTrackingEvent,
  FourPointStarIcon,
  getPurchaseRewardAmountForProduct,
  getRetailerFromId,
  isProductEligibleForCheckout,
  ProductRewardSearchResult,
  RetailerIconGroup,
  BuyBoxDialog,
  ButtonName,
} from "@brandclub/common-ui";
import _orderBy from "lodash/orderBy";

import { useTrackActions } from "../../hooks/useTracking";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { styled, useTheme } from "@mui/material";
import { formatMoney } from "../../utils/string";
import { HOST_DOMAIN } from "../../utils/domain";
import { appThemeQueryOptions } from "@/api/cache/appThemeQueryOptions";
import { useQuery } from "@tanstack/react-query";
import { useDomainConfig } from "@/api/cache/appConfigQueryOptions";
import { useCurrentBrandclubProduct } from "@/api/cache/dtcProductQueryOptions";
import { useAllCarts, useCartUpdates } from "@/api/cache/cartQueryOptions";
import BagDrawer from "../BagButton/BagDrawer";
import PurchaseRewardDialog from "@/components/dialog/PurchaseRewardDialog";
import { ShopAppButton } from "@/components/button/ShopAppButton";

const LocationMap = lazy(() => import("./LocationMap"));

const DEFAULT_ADD_TO_BAG_TEXT = "Add to bag  •  ${price}";

const MoreRetailersInfoContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  marginTop: "24px",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  gap: "12px",

  ".bc_more_retailers_info_text": {
    fontSize: "16px",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
  },
}));

const MoreRetailersInfo = ({
  product,
  handleBuyBoxBuyClick,
}: {
  product?: ProductRewardSearchResult;
  handleBuyBoxBuyClick: (product: ProductRewardSearchResult) => void;
}) => {
  const currentRetailerId = product?.retailerId;
  const availableRetailerIds = product?.matchedProducts?.map(
    (product) => product.retailerId
  );
  const handleViewAllRetailers = () => {
    let productsToList = [
      {
        ...product,
      },
    ];
    product?.matchedProducts?.forEach((matchedProduct: any) => {
      productsToList.push({
        ...matchedProduct,
      });
    });
    productsToList = _orderBy(
      productsToList,
      (p) => {
        return (p.retailPrice || 0) - (p.totalRewardAmount || 0);
      },
      ["asc"]
    );

    BuyBoxDialog.show({
      products: productsToList,
      onClick: handleBuyBoxBuyClick,
      onProductPageClick: (product) => {
        window.open(product.retailerProductPageUrl, "_blank");
      },
    });
  };
  if (availableRetailerIds?.length && availableRetailerIds.length > 0) {
    return (
      <MoreRetailersInfoContainer
        sx={{ cursor: "pointer" }}
        onClick={handleViewAllRetailers}
      >
        <RetailerIconGroup
          retailerIds={
            currentRetailerId
              ? [currentRetailerId, ...availableRetailerIds]
              : availableRetailerIds
          }
          iconSize={24}
        />
        <div className="bc_more_retailers_info_text">
          View all retail offers
        </div>
      </MoreRetailersInfoContainer>
    );
  } else if (currentRetailerId) {
    return (
      <MoreRetailersInfoContainer>
        <RetailerIconGroup retailerIds={[currentRetailerId]} iconSize={24} />
        <div className="bc_more_retailers_info_text">
          Expedited shipping from {getRetailerFromId(currentRetailerId)}
        </div>
      </MoreRetailersInfoContainer>
    );
  } else {
    return null;
  }
};

const AddToBagButtonContainer = styled("div", {
  name: "AddToBagButtonContainer",
  slot: "Root",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "16px",
  // Match [0, xs)
  [theme.breakpoints.down("xs")]: {
    width:
      theme.componentCSSOverrides?.[ButtonName.AddToBagButton]?.xs?.width ||
      "fit-content",
  },
  // Match [xs, ∞)
  [theme.breakpoints.up("xs")]: {
    width:
      theme.componentCSSOverrides?.[ButtonName.AddToBagButton]?.xs?.width ||
      "fit-content",
  },
  // Match [md, ∞)
  [theme.breakpoints.up("md")]: {
    width:
      theme.componentCSSOverrides?.[ButtonName.AddToBagButton]?.md?.width ||
      "fit-content",
  },
}));

const ScrollSliderContainer = styled("div")(({ theme }) => ({
  width: "100vw",
  position: "fixed",
  bottom: 0,
  left: 0,
  boxShadow: "0 3px 6px 0 rgba(31, 27, 27, 0.16)",
  ".scroll-slider-inner": {
    gap: "14px",
    borderTop: "1px solid rgba(220, 221, 220, 0.5)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "17px 34px",
    width: "100%",
    backgroundColor: theme.vars?.palette?.background?.default,
    color: theme.vars?.palette?.text?.primary,
    fontSize: "12px",
    fontWeight: "bold",
  },
  transition: "transform 0.6s",
  zIndex: 100,
  "&.scroll-on": {
    transform: "translateY(0)",
  },
  "&.scroll-off": {
    transform: "translateY(105%)",
  },
}));

const AddToBagButton = forwardRef<
  HTMLDivElement,
  {
    mobile?: boolean;
    currentBrandclubProduct?: ProductRewardSearchResult;
    handleBuyClick: () => void;
    isEligibleForCheckout: boolean;
  }
>(
  (
    { currentBrandclubProduct, handleBuyClick, mobile, isEligibleForCheckout },
    ref
  ) => {
    const theme = useTheme();
    const paramName = mobile ? "buyButtonMobile" : "buyButton";
    const [trackCheckoutBarAction] = useTrackActions();

    const { data: cartProducts } = useAllCarts({
      select: (data) => data.products,
      // only read from cache to avoid resetting updated local carts
      enabled: false, // Prevents fetching
      staleTime: Infinity, // Prevents background refetches
    });
    const { data: styleData } = useQuery(appThemeQueryOptions());

    if (!currentBrandclubProduct) {
      return null;
    }

    const addToBagText =
      theme.content?.addToBagButton?.signedOutDesktop?.text ||
      DEFAULT_ADD_TO_BAG_TEXT;

    const priceAmount = formatMoney(currentBrandclubProduct?.retailPrice || 0);

    const displayAddToBagText = isEligibleForCheckout
      ? addToBagText

          .replace(
            "${price}",
            getRetailerFromId(currentBrandclubProduct?.retailerId) +
              " " +
              priceAmount
          )
          .replace(/\s+/g, " ")
          .trim()
      : "View";

    const cartProduct = currentBrandclubProduct?.stacklineSku
      ? cartProducts?.[currentBrandclubProduct?.stacklineSku]
      : currentBrandclubProduct;

    const metadata = cartProduct ?? currentBrandclubProduct;

    const rewardAmount =
      metadata && getPurchaseRewardAmountForProduct(metadata);

    const addToBagStyleOverride =
      theme.componentCSSOverrides?.[ButtonName.AddToBagButton];

    const height = {
      xs: addToBagStyleOverride?.xs?.height,
      md: addToBagStyleOverride?.md?.height,
    };

    const earnRewardButtonStyleOverride = addToBagStyleOverride;
    delete earnRewardButtonStyleOverride?.md?.color;
    delete earnRewardButtonStyleOverride?.xs?.color;

    return (
      <AddToBagButtonContainer ref={ref}>
        <ShopAppButton
          height={height}
          sx={{
            width: "100%",
            ...addToBagStyleOverride,
            // need to use && here because ShopAppButton uses `increaseSpecificity` to avoid having styles overwritten by DTC sites
            "&&": {
              font: theme.vars.font.lgButton,
              fontWeight: theme.typography.lgButton.fontWeight,
              [theme.breakpoints.down("xs")]: {
                font: theme.vars.font.button,
                fontWeight: theme.typography.button.fontWeight,
              },
            },
          }}
          variant="solid"
          onClick={() => {
            handleBuyClick();
          }}
          type="button"
        >
          {displayAddToBagText}
        </ShopAppButton>
        {!!rewardAmount && (
          <ShopAppButton
            height={height}
            sx={{
              width: "100%",
              ...earnRewardButtonStyleOverride,
              font: theme.vars.font.lgButton,
              fontWeight: theme.typography.lgButton.fontWeight,
              [theme.breakpoints.down("xs")]: {
                font: theme.vars.font.button,
                fontWeight: theme.typography.button.fontWeight,
              },
            }}
            variant="outline"
            onClick={() => {
              PurchaseRewardDialog.show(rewardAmount);
              trackCheckoutBarAction(
                AnalyticsTrackingEvent.DTC_VIEW_PURCHASE_BENEFITS,
                {
                  stacklineSku: currentBrandclubProduct?.stacklineSku,
                  retailerId: currentBrandclubProduct?.retailerId,
                }
              );
            }}
            type="button"
          >
            <FourPointStarIcon
              style={{ width: 16, height: 16, marginRight: 8 }}
            />
            Earn {formatMoney(rewardAmount)} in cash rewards
          </ShopAppButton>
        )}
      </AddToBagButtonContainer>
    );
  }
);

const InpageAddToBagButton = ({ mobile }: { mobile?: boolean }) => {
  const theme = useTheme();
  const paramName = mobile ? "buyButtonMobile" : "buyButton";
  const [isScrollSliderOn, setIsScrollSliderOn] = useState(false);
  const [isBuyBoxOpen, setIsBuyBoxOpen] = useState(false);
  const inpageButtonRef = useRef<HTMLDivElement | null>(null);
  const { mutate: updateCart } = useCartUpdates();

  useEffect(() => {
    const handleScroll = () => {
      if (inpageButtonRef.current) {
        const rect = inpageButtonRef.current.getBoundingClientRect();
        setIsScrollSliderOn(rect.top < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const enableScrollSliderDisplay =
    !!theme.settings?.featureFlags?.scrollSliderDisplay;
  const enableLocationMap = !!theme.settings?.featureFlags?.storeLocatorMap;

  const bcRouting = useBCRouting();
  const [trackCheckoutBarAction] = useTrackActions();

  const { data: domainConfig } = useDomainConfig();

  const { data: currentBrandclubProduct } = useCurrentBrandclubProduct();

  const isEligibleForCheckout =
    !!currentBrandclubProduct &&
    isProductEligibleForCheckout(
      currentBrandclubProduct,
      domainConfig?.checkoutAllowedOfferType
    );

  const handleBuyClick = (product: ProductRewardSearchResult) => {
    if (!product) {
      return;
    }

    if (isEligibleForCheckout) {
      updateCart({
        action: "add",
        items: [{ ...product, quantity: 1 }],
        retailerId: product.retailerId,
      });
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART,
        {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          quantity: 1,
          source: "brandclub_dtc_checkout_bar",
        }
      );
      BagDrawer?.show();
    } else {
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE,
        {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          source: "brandclub_dtc_checkout_bar",
        }
      );

      bcRouting.navigate(product.retailerProductPageUrl, "_blank");
    }
  };

  const availableRetailerIds = currentBrandclubProduct?.matchedProducts?.map(
    (product) => product.retailerId
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",

          width: "100%",
        }}
      >
        <AddToBagButton
          ref={inpageButtonRef}
          mobile={mobile}
          handleBuyClick={() => {
            handleBuyClick(
              currentBrandclubProduct as ProductRewardSearchResult
            );
          }}
          currentBrandclubProduct={currentBrandclubProduct}
          isEligibleForCheckout={isEligibleForCheckout}
        />
        <MoreRetailersInfo
          handleBuyBoxBuyClick={handleBuyClick}
          product={currentBrandclubProduct}
        />
        {enableLocationMap ? (
          <div style={{ marginTop: "30px" }}>
            <Suspense fallback={null}>
              <LocationMap
                mobile={mobile}
                stacklineSku={currentBrandclubProduct?.stacklineSku}
                API_URL={HOST_DOMAIN}
                product={currentBrandclubProduct}
                onAddProductToBag={() => {
                  currentBrandclubProduct?.matchedProducts?.length
                    ? BuyBoxDialog.show({
                        products: [
                          {
                            ...currentBrandclubProduct,
                          },
                          ...currentBrandclubProduct?.matchedProducts,
                        ],
                        onClick: handleBuyClick,
                        onProductPageClick: (product) => {
                          window.open(product.retailerProductPageUrl, "_blank");
                        },
                      })
                    : handleBuyClick(
                        currentBrandclubProduct as ProductRewardSearchResult
                      );
                }}
              />
            </Suspense>
          </div>
        ) : null}
      </div>
      {/* // if the buybox is open, we don't want to show the scroll slider */}
      {enableScrollSliderDisplay && !isBuyBoxOpen && currentBrandclubProduct ? (
        <ScrollSliderContainer
          className={isScrollSliderOn ? "scroll-on" : "scroll-off"}
        >
          <div className="scroll-slider-inner">
            <AddToBagButton
              mobile={mobile}
              handleBuyClick={() => {
                handleBuyClick(currentBrandclubProduct);
              }}
              currentBrandclubProduct={currentBrandclubProduct}
              isEligibleForCheckout={isEligibleForCheckout}
            />
          </div>
        </ScrollSliderContainer>
      ) : null}
    </>
  );
};

export default InpageAddToBagButton;
